import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { motion } from "framer-motion";
import _ from "lodash";
import useToggle from "../../Hooks/useToggle";
import navigationContext from "../../contexts/navigationsContext";
import { navigations } from "../../Data/navigation.json";
import Drawer from "../Mobile/Drawer";
import Footer from "../Partials/Footers/HomeOne/Footer";
import HeaderOne from "../Partials/Headers/HeaderOne";
import BackToTop from "../Helpers/BackToTop";
import { getProductData } from "../../services/HomeServices";
import { addToCart } from "../../services/AuthServices";
import { getUserId, isLogin } from "../../lib/constant";
import noImageFound from "../../assets/images/no-image-found.jpg";
import "./ProductsPage.css";

const ProductsPage = () => {
  const navigationData = navigations;
  const [drawer, setDrawer] = useToggle(false);
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const navigate = useNavigate();
  const perPage = 12;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    getData();
  }, [currentPage]);

  const getData = async () => {
    setIsLoading(true);
    try {
      const response = await getProductData(currentPage, perPage, getUserId());
      setData(response?.data?.data || []);
      const total = response?.data?.total || 0;
      setTotalPages(Math.ceil(total / perPage) || 1);
    } catch (error) {
      console.error("Error fetching products:", error);
      setData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const getDiscountPercent = (basePrice, discountPrice) => {
    if (!basePrice || !discountPrice || basePrice <= discountPrice) return 0;
    return Math.round(((basePrice - discountPrice) / basePrice) * 100);
  };

  const updateQty = async (id, qty, existingQty, stock) => {
    if (!isLogin()) {
      toast("Please login to add items to cart", { type: "warning" });
      navigate("/login");
      return;
    }

    if (qty < 0) {
      return toast("Please enter proper Quantity", { type: "error" });
    } else if (qty > stock + 1) {
      return toast("Please enter proper Quantity", { type: "error" });
    }

    try {
      let formdata = new FormData();
      formdata.append("product_id", id);
      formdata.append("quantity", qty);

      await addToCart(formdata);

      const updatedData = data.map((a) => {
        const aId = a?.id || a?.product_id;
        return aId === id ? { ...a, qty: qty } : a;
      });
      setData(updatedData);
      toast("Product added to cart Successfully", { type: "success" });
    } catch (error) {
      const message = error?.response?.data?.message || "Something went wrong";
      toast(message, { type: "error" });
    }
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <Drawer
        drawer={drawer}
        action={setDrawer.toggle}
        navigationData={navigationData}
      />
      <navigationContext.Provider value={{ navigationData }}>
        <HeaderOne drawerAction={setDrawer.toggle} />
      </navigationContext.Provider>

      <section className="products-page">
        <div className="products-page-container">
          <div className="products-page-header">
            <div className="products-page-title">
              <div className="products-page-icon">
                <i className="fas fa-shopping-bag"></i>
              </div>
              <div>
                <h1>Our Products</h1>
                <p>Discover amazing products at great prices!</p>
              </div>
            </div>
          </div>

          <div className="products-page-content">
            {isLoading ? (
              <div className="products-loading">
                <div className="products-spinner"></div>
              </div>
            ) : !_.isEmpty(data) ? (
              <>
                <div className="products-page-grid">
                  {data.map((product, i) => {
                    const productId = product?.id || product?.product_id;
                    return (
                      <div
                        className="products-page-card"
                        key={productId || i}
                        onClick={() => productId && navigate(`/product/${productId}`, { state: { product } })}
                      >
                        <div className="products-page-card-image">
                          <img
                            src={product?.image || noImageFound}
                            alt={product?.name || "Product"}
                            loading="lazy"
                            onError={(e) => { e.target.src = noImageFound; }}
                          />
                          {getDiscountPercent(product?.base_price, product?.discount_price) > 0 && (
                            <span className="products-page-discount-badge">
                              {getDiscountPercent(product?.base_price, product?.discount_price)}%
                            </span>
                          )}
                        </div>

                        <div className="products-page-card-content">
                          <h3 className="products-page-card-name">{product?.name || "Product"}</h3>
                          <p className="products-page-card-description">
                            {product?.description || "No description available"}
                          </p>

                          <div className="products-page-card-pricing">
                            {product?.base_price !== product?.discount_price && (
                              <span className="products-page-original-price">
                                ₹{product?.base_price}
                              </span>
                            )}
                            <span className="products-page-discount-price">
                              ₹{product?.discount_price || product?.base_price || 0}
                            </span>
                          </div>

                          <div className="products-page-card-actions" onClick={(e) => e.stopPropagation()}>
                            <button
                              className="products-page-qty-btn"
                              onClick={() =>
                                updateQty(
                                  productId,
                                  Number(product?.qty || 0) - 1,
                                  product?.qty,
                                  product?.stock
                                )
                              }
                              disabled={!product?.qty || product?.qty <= 0}
                            >
                              -
                            </button>
                            <span className="products-page-qty-display">{product?.qty || 0}</span>
                            <button
                              className="products-page-qty-btn"
                              onClick={() =>
                                updateQty(
                                  productId,
                                  Number(product?.qty || 0) + 1,
                                  product?.qty,
                                  product?.stock
                                )
                              }
                              disabled={product?.qty >= product?.stock}
                            >
                              +
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {totalPages > 1 && (
                  <div className="products-pagination">
                    <button
                      className="pagination-btn"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      <i className="fas fa-chevron-left"></i>
                    </button>
                    {[...Array(totalPages)].map((_, index) => (
                      <button
                        key={index + 1}
                        className={`pagination-btn ${currentPage === index + 1 ? 'active' : ''}`}
                        onClick={() => handlePageChange(index + 1)}
                      >
                        {index + 1}
                      </button>
                    ))}
                    <button
                      className="pagination-btn"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      <i className="fas fa-chevron-right"></i>
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="products-empty-state">
                <div className="products-empty-icon">
                  <i className="fas fa-box-open"></i>
                </div>
                <h3>No Products Available</h3>
                <p>Check back later for exciting products!</p>
              </div>
            )}
          </div>
        </div>
      </section>

      <Footer />
      <BackToTop />
    </motion.div>
  );
};

export default ProductsPage;
